import { useHistory } from "react-router";

import React, { useCallback, useMemo } from "react";

import { FormControl, FormGroup, Theme, makeStyles } from "@material-ui/core";

import clsx from "clsx";

import _ from "lodash";

import {
  RequestableSaasDestination,
  SaaSDestination,
  WarehouseDestination,
} from "../../../destinations/destinations";
import {
  LoadedDestinationInfo,
  LoadedEventDefinition,
} from "../../../types/global";
import useArrayOfDestinations from "../../../hooks/useArrayOfDestinations";
import { eventCategories, eventDefType } from "../../../eventTypes";

import { EmptyIcon } from "../../../components/icons/themes/logos";
import FilterSelectMenu from "./FilterSelectMenu";
import {
  DESTINATIONS_FILTER_KEY,
  TAG_FILTER_KEY,
  TYPE_FILTER_KEY,
  VISIBILITY_FILTER_KEY,
  VisibilityFilterOptionValues,
  visibilityFilterOptions,
} from "./constants";
import SearchBox from "../../../components/SearchBox";

const useStyles = makeStyles((theme: Theme) => ({
  buttonGroup: {
    flexDirection: "row",
    gap: theme.spacing(2),
  },
}));

type DestinationMenuItemLabelProps = {
  destination: LoadedDestinationInfo;
  configurableDestinations: Record<
    string,
    | SaaSDestination
    | RequestableSaasDestination
    | WarehouseDestination
    | undefined
  >;
};

function DestinationMenuItemLabel({
  destination,
  configurableDestinations,
}: DestinationMenuItemLabelProps) {
  const destMetadata = configurableDestinations[destination.type_slug] as
    | SaaSDestination
    | RequestableSaasDestination
    | WarehouseDestination;

  const Logo = destMetadata?.logoSquare || EmptyIcon;

  return (
    <div className="flex items-start">
      <span className="mr-2">
        <Logo />
      </span>
      <div>
        {destMetadata?.name}
        {destMetadata instanceof SaaSDestination &&
          destMetadata.supportsMultiConfig && (
            <>
              <br />
              <small>
                {destMetadata.multiConfigLabel}: {destination.instance_name}
              </small>
            </>
          )}
      </div>
    </div>
  );
}

type FilterEventsGroupProps = {
  allEvents: LoadedEventDefinition[];
  typeFilterChoices: string[];
  tagFilterChoices: string[];
  visibilityFilterChoices: VisibilityFilterOptionValues[];
  destinationFilterChoices: string[];
  configurableDestinations: Record<
    string,
    | SaaSDestination
    | RequestableSaasDestination
    | WarehouseDestination
    | undefined
  >;
  setSearchQuery: (searchQuery: string) => void;
  searchQuery: string;
};

function FilterEventsGroup({
  allEvents,
  typeFilterChoices,
  tagFilterChoices,
  visibilityFilterChoices,
  destinationFilterChoices,
  configurableDestinations,
  setSearchQuery,
  searchQuery,
}: FilterEventsGroupProps) {
  const classes = useStyles();
  const history = useHistory();
  const destinations = useArrayOfDestinations();

  const applySearchQuery = useCallback(
    (url: URLSearchParams) => {
      history.push(window.location.pathname + "?" + url.toString());
    },
    [history]
  );

  // This method will modify the URL query string to reflect the changes to
  // filter values. It will also remove the filter key if the filter is empty.
  const applyFilter = useCallback(
    (filterKey: string, newFilter: string[]) => {
      const url = new URLSearchParams(window.location.search);

      if (newFilter.length === 0) {
        url.delete(filterKey);
      } else {
        url.set(filterKey, newFilter.join(","));
      }

      applySearchQuery(url);
    },
    [applySearchQuery]
  );

  const tagFilterOptions = useMemo(
    () =>
      _(allEvents)
        .flatMap((ev) => _.keys(ev.tags))
        .uniq()
        .sortBy()
        .map((tag) => ({
          label: tag,
          value: tag,
        }))
        .value(),
    [allEvents]
  );

  const typeFilterOptions = useMemo(
    () =>
      eventCategories
        // Limit the types of events the user can filter on in the UI to only
        // those events they have configured.
        .filter((eventCategory) => {
          return _.some(
            allEvents,
            (event) => eventDefType(event) === eventCategory.type
          );
        })
        .map(({ name, type }) => ({
          label: name,
          value: type,
        })),
    [allEvents]
  );

  const destIDsUsedByEvents = useMemo(() => {
    return new Set(
      _.flatMap(allEvents, (event) => _.keys(event.enabled_dests))
    );
  }, [allEvents]);

  const destinationOptions = useMemo(() => {
    return destinations
      .filter((dest) => destIDsUsedByEvents.has(dest.id) || dest.enabled)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [destinations, destIDsUsedByEvents]);

  const destinationNamesByIDs = useMemo(() => {
    return destinationOptions.reduce((acc, dest) => {
      return {
        ...acc,
        [dest.id]: dest.name,
      };
    }, {} as Record<string, string>);
  }, [destinationOptions]);

  const hasHiddenEvents = useMemo(
    () => _.some(allEvents, (event) => event.is_hidden),
    [allEvents]
  );

  return (
    <FormGroup className={clsx(classes.buttonGroup)}>
      <FormControl size="small">
        <FilterSelectMenu
          label="Event Types"
          onSelectValue={(selectedTypes) => {
            applyFilter(TYPE_FILTER_KEY, selectedTypes);
          }}
          onSelectAll={() => {
            applyFilter(
              TYPE_FILTER_KEY,
              typeFilterOptions.map((o) => o.value)
            );
          }}
          onClear={() => applyFilter(TYPE_FILTER_KEY, [])}
          options={typeFilterOptions}
          selectedValues={typeFilterChoices}
          optionValueAsHTMLAttrString={(o) => o}
          selectedValueAsDisplayString={(o) => o}
        />
      </FormControl>

      <FormControl size="small">
        <FilterSelectMenu
          label="Tags"
          onSelectValue={(selectedTags) => {
            applyFilter(TAG_FILTER_KEY, selectedTags);
          }}
          onSelectAll={() => {
            applyFilter(
              TAG_FILTER_KEY,
              tagFilterOptions.map((o) => o.value)
            );
          }}
          onClear={() => applyFilter(TAG_FILTER_KEY, [])}
          options={tagFilterOptions}
          selectedValues={tagFilterChoices}
          optionValueAsHTMLAttrString={(o) => o}
          selectedValueAsDisplayString={(o) => o}
        />
      </FormControl>

      <FormControl size="small">
        <FilterSelectMenu
          label="Destinations"
          onSelectValue={(selectedDestinations) => {
            applyFilter(DESTINATIONS_FILTER_KEY, selectedDestinations);
          }}
          onSelectAll={() => {
            applyFilter(
              DESTINATIONS_FILTER_KEY,
              destinations.map((o) => o.id)
            );
          }}
          onClear={() => applyFilter(DESTINATIONS_FILTER_KEY, [])}
          options={Object.values(destinationOptions).map((d) => {
            const dest = d as LoadedDestinationInfo;

            return { label: dest.name, value: dest };
          })}
          selectedValues={destinationFilterChoices}
          renderMenuItemLabel={(o) => {
            return (
              <DestinationMenuItemLabel
                destination={o.value}
                configurableDestinations={configurableDestinations}
              />
            );
          }}
          optionValueAsHTMLAttrString={(o) => o.id}
          selectedValueAsDisplayString={(selectedDestID) => {
            return destinationNamesByIDs[selectedDestID];
          }}
        />
      </FormControl>

      {hasHiddenEvents && (
        <FormControl size="small">
          <FilterSelectMenu
            label="Event Visibility"
            onSelectValue={(selectedVisibility) => {
              applyFilter(VISIBILITY_FILTER_KEY, selectedVisibility);
            }}
            onSelectAll={() => {
              applyFilter(
                VISIBILITY_FILTER_KEY,
                visibilityFilterOptions.map((o) => o.value)
              );
            }}
            onClear={() => applyFilter(VISIBILITY_FILTER_KEY, [])}
            options={visibilityFilterOptions}
            selectedValues={visibilityFilterChoices}
            optionValueAsHTMLAttrString={(o) => o}
            selectedValueAsDisplayString={(o) => o}
          />
        </FormControl>
      )}

      <SearchBox
        onChange={(e) => setSearchQuery(e.target.value)}
        searchQuery={searchQuery}
        placeholder="Search Events"
      />
    </FormGroup>
  );
}

export default FilterEventsGroup;
